import { NavigateFunction } from 'react-router-dom';

import { UUID } from '@/utils/uuid';

// Pure path builders (use in anchors, window.open, emails, etc.)
export function pathToAuthLogin() {
  return '/login';
}

export function pathToAuthLogout() {
  return '/logout';
}

export function pathToAppStart() {
  return '/';
}

export function pathToProfile() {
  return '/profile';
}

export function pathToProfileSettings() {
  return '/profile/settings/general';
}

export function pathToProfileSecurity() {
  return '/profile/security';
}

export function pathToTenant(tenantSlug: string) {
  return `/${tenantSlug}`;
}

// Cross-tenant staff administration. Not tenant-scoped by design, so this takes
// no slug.
export function pathToAdmin() {
  return '/admin';
}

export function pathToAdminFieldMappings() {
  return '/admin/field-mappings';
}

export function pathToAdminAIUsage() {
  return '/admin/ai-usage';
}

export function pathToAdminMeshOutbox() {
  return '/admin/mesh-outbox';
}

export function pathToAdminMeshConnectivity() {
  return '/admin/mesh-connectivity';
}

export function pathToAdminTenants() {
  return '/admin/tenants';
}

export function pathToAdminProvisionTenant() {
  return '/admin/tenants/new';
}

export function pathToAdminTenantAuth(tenantId: string) {
  return `/admin/tenants/${encodeURIComponent(tenantId)}/auth`;
}

export function pathToAdminStaff() {
  return '/admin/staff';
}

export function pathToAdminFeatureFlags() {
  return '/admin/feature-flags';
}

export function pathToExploreSearch(tenantSlug: string) {
  return `/${tenantSlug}/explore`;
}

export function pathToActivities(tenantSlug: string) {
  return `/${tenantSlug}/explore/activities`;
}

export function pathToApplications(tenantSlug: string) {
  return `/${tenantSlug}/explore/applications`;
}

export function pathToApplication(tenantSlug: string, applicationId: string) {
  return `/${tenantSlug}/explore/applications/${applicationId}`;
}

export function pathToAccounts(tenantSlug: string) {
  return `/${tenantSlug}/explore/accounts`;
}

export function pathToAccount(tenantSlug: string, accountId: string) {
  return `/${tenantSlug}/explore/accounts/${accountId}`;
}

export function pathToOwners(tenantSlug: string) {
  return `/${tenantSlug}/explore/owners`;
}

export function pathToOwner(tenantSlug: string, ownerId: string) {
  return `/${tenantSlug}/explore/owners/${ownerId}`;
}

export function pathToGroups(tenantSlug: string) {
  return `/${tenantSlug}/explore/groups`;
}

export function pathToGroup(tenantSlug: string, groupId: string) {
  return `/${tenantSlug}/explore/groups/${groupId}`;
}

// Settings
export function pathToSettings(tenantSlug: string) {
  return `/${tenantSlug}/manage`;
}

export function pathToSettingsGeneral(tenantSlug: string) {
  return `/${tenantSlug}/manage/general`;
}

export function pathToSettingsClients(tenantSlug: string) {
  return `/${tenantSlug}/manage/clients`;
}

export function pathToSettingsSecrets(tenantSlug: string) {
  return `/${tenantSlug}/manage/secrets`;
}

export function pathToSettingsRoles(tenantSlug: string) {
  return `/${tenantSlug}/manage/roles`;
}

export function pathToSettingsRoleEdit(tenantSlug: string, roleId: string) {
  return `/${tenantSlug}/manage/roles/${roleId}/settings`;
}

export function pathToSettingsTeams(tenantSlug: string) {
  return `/${tenantSlug}/manage/teams`;
}

export function pathToSettingsTeamCreate(tenantSlug: string) {
  return `/${tenantSlug}/manage/teams/create`;
}

export function pathToSettingsTeam(tenantSlug: string, teamId: UUID) {
  return `/${tenantSlug}/manage/teams/${teamId}`;
}

export function pathToSettingsTeamEdit(tenantSlug: string, teamId: UUID) {
  return `/${tenantSlug}/manage/teams/${teamId}/settings`;
}

export function pathToSettingsMembers(tenantSlug: string) {
  return `/${tenantSlug}/manage/members`;
}

export function pathToSettingsMemberEdit(tenantSlug: string, userId: UUID) {
  return `/${tenantSlug}/manage/members/${userId}/configure`;
}

export function pathToSettingsInvitations(tenantSlug: string) {
  return `/${tenantSlug}/manage/invitations`;
}

export function pathToConnectors(tenantSlug: string) {
  return `/${tenantSlug}/manage/connectors`;
}

export function pathToProviders(tenantSlug: string) {
  return `/${tenantSlug}/manage/providers`;
}

export function pathToSettingsProviderEdit(
  tenantSlug: string,
  providerId: UUID
) {
  return `/${tenantSlug}/manage/providers/${providerId}/settings`;
}

export function pathToProviderFeatureCreate(
  tenantSlug: string,
  providerId: UUID
) {
  return `/${tenantSlug}/manage/providers/${providerId}/features/create`;
}

export function pathToProviderFeatureEdit(
  tenantSlug: string,
  providerId: UUID,
  featureId: UUID
) {
  return `/${tenantSlug}/manage/providers/${providerId}/features/${featureId}/configure`;
}

export function pathToSecrets(tenantSlug: string) {
  return `/${tenantSlug}/manage/secrets`;
}

export function pathToSecretDetail(tenantSlug: string, secretId: string) {
  return `/${tenantSlug}/manage/secrets/${secretId}/details`;
}

export function pathToSensitiveContent(tenantSlug: string) {
  return `/${tenantSlug}/manage/sensitive-content`;
}

export function pathToCreateSecretTemplate(tenantSlug: string) {
  return `/${tenantSlug}/manage/secrets/templates/create`;
}

/** View-only detail page for a secret template */
export function pathToSecretTemplateDetail(
  tenantSlug: string,
  templateId: string
) {
  return `/${tenantSlug}/manage/secrets/templates/${templateId}`;
}

/** Edit form for a secret template */
export function pathToSecretTemplateEdit(
  tenantSlug: string,
  templateId: string
) {
  return `/${tenantSlug}/manage/secrets/templates/${templateId}/configure`;
}

export function pathToConnector(tenantSlug: string, connectorId: string) {
  return `/${tenantSlug}/manage/connectors/${connectorId}`;
}

export function pathToSettingsConnectorEdit(
  tenantSlug: string,
  connectorId: UUID
) {
  return `/${tenantSlug}/manage/connectors/${connectorId}/view`;
}

export function pathToConnectorFeatureCreate(
  tenantSlug: string,
  connectorId: UUID
) {
  return `/${tenantSlug}/manage/connectors/${connectorId}/features/create`;
}

function connectorFeatureBasePath(
  tenantSlug: string,
  connectorId: UUID,
  featureId: UUID
) {
  return `/${tenantSlug}/manage/connectors/${connectorId}/features/${featureId}/view`;
}

export function pathToConnectorFeatureRun(
  tenantSlug: string,
  connectorId: UUID,
  featureId: UUID,
  runSequence?: number
) {
  const base = pathToConnectorFeatureRuns(tenantSlug, connectorId, featureId);
  return runSequence !== undefined
    ? `${base}?run=${encodeURIComponent(String(runSequence))}`
    : base;
}

export function pathToConnectorFeatureRuns(
  tenantSlug: string,
  connectorId: UUID,
  featureId: UUID
) {
  return connectorFeatureBasePath(tenantSlug, connectorId, featureId);
}

export function pathToConnectorFeatureHistory(
  tenantSlug: string,
  connectorId: UUID,
  featureId: UUID
) {
  return `${connectorFeatureBasePath(tenantSlug, connectorId, featureId)}/previous-runs`;
}

export function pathToConnectorFeatureConfiguration(
  tenantSlug: string,
  connectorId: UUID,
  featureId: UUID
) {
  // Configuration is a dialog on the feature view page; the `configure` param
  // tells the page to open it on load.
  return `${connectorFeatureBasePath(tenantSlug, connectorId, featureId)}?configure=1`;
}

export function pathToConnectorCatalogEntity(
  tenantSlug: string,
  connectorId: UUID,
  entitySlug: string
) {
  return `/${tenantSlug}/manage/connectors/${connectorId}/entities/${encodeURIComponent(entitySlug)}`;
}

export function pathToConnectorLogs(tenantSlug: string, connectorId: UUID) {
  return `/${tenantSlug}/manage/connectors/${connectorId}/view/logs`;
}

export function pathToTenantLogs(tenantSlug: string) {
  return `/${tenantSlug}/logs`;
}

export function pathToSetup(tenantSlug: string) {
  return `/${tenantSlug}/setup`;
}

export function pathToSetupConfigure(tenantSlug: string, connectorId: string) {
  return `/${tenantSlug}/setup/configure/${connectorId}`;
}

export function pathToConnectorWizard(tenantSlug: string, connectorId: string) {
  return `/${tenantSlug}/settings/connectors/${connectorId}/wizard`;
}

export function pathToCatalog(tenantSlug: string) {
  return `/${tenantSlug}/catalog`;
}

export function pathToClients(tenantSlug: string) {
  return `/${tenantSlug}/manage/clients`;
}

export function pathToEnrollment(tenantSlug: string) {
  return `/${tenantSlug}/manage/enrollment`;
}

export function pathToClientDetail(tenantSlug: string, clientId: string) {
  return `/${tenantSlug}/manage/clients/${clientId}/details`;
}

export function navToClients(
  navigate: NavigateFunction,
  tenantSlug: string,
  opts?: { replace?: boolean }
) {
  navigate(pathToClients(tenantSlug), opts);
}

// Engine
export function pathToEngine(tenantSlug: string) {
  return `/${tenantSlug}/engine`;
}

export function pathToEngineConfig(tenantSlug: string) {
  return `/${tenantSlug}/engine/config`;
}

export function pathToEngineRunners(tenantSlug: string) {
  return `/${tenantSlug}/engine/runners`;
}

export function pathToEngineEvents(tenantSlug: string) {
  return `/${tenantSlug}/engine/events`;
}

export function pathToEvaluators(tenantSlug: string) {
  return `/${tenantSlug}/engine/evaluators`;
}

export function pathToEvaluator(tenantSlug: string, ruleId: UUID) {
  return `/${tenantSlug}/engine/evaluators/${ruleId}`;
}

export function pathToEvaluatorCreate(tenantSlug: string) {
  return `/${tenantSlug}/engine/evaluators/create`;
}

export function pathToEvaluatorEdit(tenantSlug: string, ruleId: UUID) {
  return `/${tenantSlug}/engine/evaluators/${ruleId}/edit`;
}

export function pathToAutomations(tenantSlug: string) {
  return `/${tenantSlug}/engine/automations`;
}

export function pathToAutomation(tenantSlug: string, ruleId: UUID) {
  return `/${tenantSlug}/engine/automations/${ruleId}`;
}

export function pathToAutomationCreate(tenantSlug: string) {
  return `/${tenantSlug}/engine/automations/create`;
}

export function pathToAutomationEdit(tenantSlug: string, ruleId: UUID) {
  return `/${tenantSlug}/engine/automations/${ruleId}/edit`;
}

// Navigation wrappers (component-friendly)
export function navTo(
  navigate: NavigateFunction,
  path: string,
  opts?: { replace?: boolean }
) {
  navigate(path, opts ?? {});
}

export function navToAuthLogin(navigate: NavigateFunction) {
  return navTo(navigate, pathToAuthLogin());
}

export function navToAuthLogout(navigate: NavigateFunction) {
  return navTo(navigate, pathToAuthLogout());
}

export function navToAppStart(navigate: NavigateFunction) {
  return navTo(navigate, pathToAppStart());
}

export function navToProfile(navigate: NavigateFunction) {
  return navTo(navigate, pathToProfile());
}

export function navToTenant(
  navigate: NavigateFunction,
  tenantSlug: string,
  opts?: { replace?: boolean }
) {
  return navTo(navigate, pathToTenant(tenantSlug), opts);
}

export function navToConnectors(
  navigate: NavigateFunction,
  tenantSlug: string,
  opts?: { replace?: boolean }
) {
  return navTo(navigate, pathToConnectors(tenantSlug), opts);
}

export function navToConnector(
  navigate: NavigateFunction,
  tenantSlug: string,
  connectorId: string,
  opts?: { replace?: boolean }
) {
  return navTo(navigate, pathToConnector(tenantSlug, connectorId), opts);
}

export function navToConnectorWizard(
  navigate: NavigateFunction,
  tenantSlug: string,
  connectorId: string,
  opts?: { replace?: boolean }
) {
  return navTo(navigate, pathToConnectorWizard(tenantSlug, connectorId), opts);
}

export function navToSecrets(
  navigate: NavigateFunction,
  tenantSlug: string,
  opts?: { replace?: boolean }
) {
  return navTo(navigate, pathToSettingsSecrets(tenantSlug), opts);
}
